import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSchool, faChalkboardTeacher, faUsers, faUserGraduate } from '@fortawesome/free-solid-svg-icons';
import DashboardService from '../../services/dashboard.service';
import { useSchool } from '../../contexts/SchoolContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';

const PrincipalDashboard = ({ user }) => {
  const { selectedSchool } = useSchool();
  const { selectedYear } = useAcademicYear();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    classes: 0,
    teachers: 0,
    students: 0,
    loadingStats: true
  });

  useEffect(() => {
    if (user?.school_users) {
      const principalSchools = user.school_users.filter(
        su => su.role === 'PRINCIPAL' && su.active
      );
      setSchools(principalSchools);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (selectedSchool && selectedYear) {
      fetchStats(selectedSchool, selectedYear);
    }
  }, [selectedSchool, selectedYear]);

  const fetchStats = async (schoolId, year) => {
    try {
      setStats(prev => ({ ...prev, loadingStats: true }));

      const data = await DashboardService.getStats({
        school: schoolId,
        year: year
      });

      setStats({
        classes: data.stats?.classes || 0,
        teachers: data.stats?.teachers || 0,
        students: data.stats?.students || 0,
        loadingStats: false
      });
    } catch (error) {
      console.error('Error fetching principal stats:', error);
      setStats({
        classes: 0,
        teachers: 0,
        students: 0,
        loadingStats: false
      });
    }
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Dashboard do Diretor</h4>
          <p className="mb-0">Bem-vindo(a), {user?.name}</p>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <Row className="mb-4">
        <Col xs={12} sm={6} xl={3} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-primary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                </Col>
                <Col xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Turmas</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.classes}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={3} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-secondary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faChalkboardTeacher} />
                  </div>
                </Col>
                <Col xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Professores</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.teachers}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={3} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-tertiary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faUserGraduate} />
                  </div>
                </Col>
                <Col xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Alunos</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.students}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={3} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-info rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faSchool} />
                  </div>
                </Col>
                <Col xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Escolas</h5>
                    <h3 className="mb-0">{schools.length}</h3>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Escolas sob Direção</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive className="align-items-center table-flush">
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Escola</th>
                    <th scope="col">Função</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {schools.length > 0 ? (
                    schools.map((schoolUser, index) => (
                      <tr key={index}>
                        <td className="fw-bold">{schoolUser.school?.name || 'Carregando...'}</td>
                        <td>
                          <Badge bg="primary" className="badge-lg">
                            Diretor
                          </Badge>
                        </td>
                        <td>
                          <Badge bg="success" className="badge-lg">
                            Ativo
                          </Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center">
                        Nenhuma escola encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PrincipalDashboard;
